import {Injectable} from '@angular/core';
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {
    ApplySuggestionRequest,
    ApplySuggestionResponse,
    GenerateSegmentSuggestionsRequest,
    SegmentSuggestionResponse
} from "../../model/dto/api/ai-segment-suggestion.interface";
import {AiSegmentSuggestionDto} from "../../model/dto/entity/ai-segment-suggestion.dto";

/**
 * Service for AI-powered segment suggestions.
 * F-024: AI-Powered Segment Suggestions - Acceptance Criteria: Given campaign goal is specified,
 * when AI analysis runs, then suggested criteria are proposed.
 */
@Injectable({
    providedIn: 'root'
})
export class AiSegmentSuggestionService extends MvsCrudService {

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ct/aiSegmentSuggestions')
    }

    getObjectIcon(): string {
        return 'fa-solid fa-wand-magic-sparkles';
    }

    /**
     * Generates AI segment suggestions based on campaign goals.
     * F-024: Acceptance Criteria - "Given campaign goal is specified, when AI analysis runs,
     * then suggested criteria are proposed"
     */
    generateSuggestions(request: GenerateSegmentSuggestionsRequest): Observable<SegmentSuggestionResponse> {
        return this.postGeneric<SegmentSuggestionResponse>(request, '/generate');
    }

    /**
     * Applies a suggestion to a campaign.
     */
    applySuggestion(request: ApplySuggestionRequest): Observable<ApplySuggestionResponse> {
        return this.postGeneric<ApplySuggestionResponse>(request, '/apply');
    }

    /**
     * Rejects a suggestion.
     */
    rejectSuggestion(suggestionId: number, reason?: string): Observable<void> {
        const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
        return this.postGeneric<void>({}, `/${suggestionId}/reject${params}`);
    }

    /**
     * Gets suggestions for a campaign.
     */
    getSuggestionsForCampaign(campaignId: number): Observable<AiSegmentSuggestionDto[]> {
        return this.getGeneric<AiSegmentSuggestionDto[]>(`/campaign/${campaignId}`);
    }

    /**
     * Gets pending suggestions for a campaign.
     */
    getPendingSuggestionsForCampaign(campaignId: number): Observable<AiSegmentSuggestionDto[]> {
        return this.getGeneric<AiSegmentSuggestionDto[]>(`/campaign/${campaignId}/pending`);
    }

    /**
     * Gets all pending suggestions.
     */
    getAllPendingSuggestions(): Observable<AiSegmentSuggestionDto[]> {
        return this.getGeneric<AiSegmentSuggestionDto[]>('/pending');
    }

    /**
     * Gets high confidence suggestions for a campaign.
     */
    getHighConfidenceSuggestions(campaignId: number, minConfidence: number = 0.8): Observable<AiSegmentSuggestionDto[]> {
        return this.getGeneric<AiSegmentSuggestionDto[]>(`/campaign/${campaignId}/highConfidence?minConfidence=${minConfidence}`);
    }
}
